import { cn } from '@shadcn/lib/utils';
import { cva } from 'class-variance-authority';
import {
  ArrowRight,
  AlarmClockOff,
  CheckCheck,
  TrendingUp,
  Send,
  CheckCircle2,
  LineChart,
  BarChart3,
  Check,
  Clock,
  Plus,
  NotepadText,
  UserCheck,
  Users,
  X,
} from 'lucide-react';
import React from 'react';

import { Link } from '../../atoms/link/link';

type ActivityStatIcon = keyof typeof iconMap;

const iconMap = {
  alarmClockOff: AlarmClockOff,
  barChart3: BarChart3,
  check: Check,
  checkCheck: CheckCheck,
  checkCircle2: CheckCircle2,
  clock: Clock,
  lineChart: LineChart,
  notepadText: NotepadText,
  plus: Plus,
  send: Send,
  trendingUp: TrendingUp,
  userCheck: UserCheck,
  users: Users,
  x: X,
} as const;

interface IActivityStatProps {
  readonly className?: {
    container?: string;
    card?: string;
    icon?: string;
    title?: string;
    subtext?: string;
    value?: string;
    link?: string;
  };
  readonly title: string;
  readonly subtext?: string;
  readonly value: string | number;
  readonly icon?: ActivityStatIcon;
  readonly href?: string;
}

const containerVariants = cva([
  'w-full max-w-72 min-w-48',
  'px-[3px] pt-6 pb-1',
  'relative inline-flex flex-col justify-start items-center',
]);

const cardVariants = cva(
  [
    'self-stretch h-48 px-6 pt-12 pb-6',
    'flex flex-col justify-start items-center gap-2.5 overflow-hidden relative transition-colors',
    'bg-white rounded-lg shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] outline outline-[1px] outline-offset-[-1px] outline-slate-400',
  ],
  {
    variants: {
      hover: {
        true: 'hover:bg-[#dce3fe] hover:outline-[#4166fb] hover:outline-[3px] hover:outline-offset-[-3px]',
      },
    },
    defaultVariants: {
      hover: true,
    },
  }
);

const iconContainerVariants = cva(['w-12 h-12 absolute top-[4px] left-1/2', 'transform -translate-x-1/2']);

const iconBackgroundVariants = cva([
  'absolute inset-0',
  'bg-gradient-to-l from-blue-500 to-purple-300',
  'rounded-full',
]);

const iconWrapperVariants = cva([
  'w-6 h-6 absolute top-1/2 left-1/2',
  'transform -translate-x-1/2 -translate-y-1/2',
  'overflow-hidden flex items-center justify-center',
]);

const titleVariants = cva(['text-black text-base font-normal text-center']);

const subtextVariants = cva(['text-muted-foreground text-sm font-normal text-center', 'mt-1']);

const valueVariants = cva(['text-slate-950 text-5xl font-bold text-center', 'mt-2']);

const linkVariants = cva([
  'absolute bottom-4 right-4',
  'text-muted-foreground cursor-pointer transition-colors',
  'hover:text-foreground',
]);

function CardContent({
  title,
  subtext,
  value,
  shouldShowArrow,
  className,
}: {
  readonly title: string;
  readonly subtext?: string;
  readonly value: string | number;
  readonly shouldShowArrow: boolean;
  readonly href?: string;
  readonly className?: IActivityStatProps['className'];
}) {
  return (
    <>
      <div className={cn(titleVariants(), className?.title)}>{title}</div>
      {subtext && <div className={cn(subtextVariants(), className?.subtext)}>{subtext}</div>}
      <div className={cn(valueVariants(), className?.value)}>{value}</div>
      {shouldShowArrow && (
        <div className={cn(linkVariants(), className?.link)}>
          <ArrowRight size={20} />
        </div>
      )}
    </>
  );
}

function ActivityStat({ title, subtext, value, icon = 'trendingUp', href, className }: IActivityStatProps) {
  const Icon = iconMap[icon];
  const Component = href ? Link : 'div';
  return (
    <Component className={cn(containerVariants(), className?.container)} data-testid="activity-stat" href={href}>
      <div className={cn(cardVariants(), className?.card)}>
        <CardContent
          className={className}
          shouldShowArrow={Boolean(href)}
          subtext={subtext}
          title={title}
          value={value}
        />
      </div>
      <div className={cn(iconContainerVariants(), className?.icon)}>
        <div className={iconBackgroundVariants()} />
        <div className={iconWrapperVariants()}>
          <Icon className="text-white" size={20} />
        </div>
      </div>
    </Component>
  );
}

ActivityStat.displayName = 'ActivityStat';

export { ActivityStat, iconMap };
export type { ActivityStatIcon, IActivityStatProps };
